import { useEffect } from 'react';
import { APPFOLIO_PUSH_STATUS } from 'src/pages/NewBillingModule/ApplyBillingFull/AppfolioPush';
import { getLiveAttempt } from 'src/pages/NewBillingModule/ApplyBillingFull/AppfolioPush/appfolioPushConstants';
import { useStore } from 'src/stores';
import { getPropertyApGlLabel, getRecordedPropertyApGlLabel, getRecordedSummaryApGlLabel, getSummaryApGlLabel } from './apGlAccounts';

/**
 * The Accounts Payable GL account a bill's lines post to — the one they *did* post to once it has been
 * pushed, and the one they *would* post to before that.
 *
 * Those are different questions, and the difference is the point. A property's configured account can
 * be re-pointed at any time; a payable already in AppFolio stays where it posted. Showing the live
 * mapping against a pushed bill quietly misstates the ledger, which is the kind of wrong that only
 * surfaces at reconciliation.
 *
 * The configured side is resolved per line, the way the push resolves it: a utility configured with an
 * account of its own posts there, and only the rest fall back to the property's default (LB-5980). Which
 * property utility a line is comes from `servicePairs`, the bill's (property, utility) pairs, asked of
 * property-service as the push asks it. Where that cannot be answered — a caller without `ManageProperty`
 * is refused — the line falls back to its property's default.
 *
 * Two company-wide reads for the rest of the configured side rather than one per property: a bill can
 * cover several, and `createAsync` cancels the previous call on the same action, so asking per property
 * would leave every answer but the last cancelled. Both are read once for the page and answer every bill
 * opened on it.
 *
 * `isEnabled` covers the GL codes flag as well as AppFolio, because the service only fills the account
 * in while that flag is on — read with it off, every property would look unconfigured when the truth
 * is that we were not told.
 */
export const useApGlAccounts = _ref => {
  var _getLiveAttempt;
  let isEnabled = _ref.isEnabled,
    pushStatus = _ref.pushStatus,
    _ref$servicePairs = _ref.servicePairs,
    servicePairs = _ref$servicePairs === void 0 ? [] : _ref$servicePairs;
  const store = useStore();
  const glAccounts = store.appfolioGlAccounts;
  useEffect(() => {
    if (!isEnabled) return;

    // Two different actions, so they do not cancel one another.
    glAccounts.listPropertiesAsync.run();
    glAccounts.listGlAccountsAsync.run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled]);
  const pairsKey = servicePairs.map(pair => "".concat(pair.propertyId, "|").concat(pair.utilityId).toLowerCase()).sort().join(',');
  useEffect(() => {
    if (!isEnabled || !servicePairs.length) return;
    glAccounts.readPropertyUtilitiesAsync.run(servicePairs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, pairsKey]);

  /**
   * A succeeded push has accounts worth reading back, and so does a pending one: its payload is frozen
   * once appfolio-service has stored it, so it is what the payable will post to whatever the mapping says
   * since (LB-5980). `failed` and `notPushed` posted nothing and the next push resolves afresh, so the
   * configured account is the true forward-looking answer for both.
   *
   * `needsReconciliation` is deliberately left on the configured side too: it means the document's rows
   * name more than one AppFolio bill, so there is no single attempt whose accounts describe what
   * happened, and picking the newest would present one payable's accounts as the document's.
   */
  const status = pushStatus === null || pushStatus === void 0 ? void 0 : pushStatus.status;
  const readAttemptId = status === APPFOLIO_PUSH_STATUS.succeeded || status === APPFOLIO_PUSH_STATUS.pending ? (_getLiveAttempt = getLiveAttempt(pushStatus)) === null || _getLiveAttempt === void 0 ? void 0 : _getLiveAttempt.pushAttemptId : null;

  /**
   * Only recorded accounts are final. Nothing recorded is asked again when the push moves on and each time
   * the bill is opened: a pending attempt is not stored until appfolio-service takes it up, and the same
   * attempt then succeeds, so an early answer kept for good would hide what it posted.
   */
  useEffect(() => {
    var _glAccounts$getRecord;
    if (!isEnabled || !readAttemptId) return;
    if ((_glAccounts$getRecord = glAccounts.getRecordedAccounts(readAttemptId)) !== null && _glAccounts$getRecord !== void 0 && _glAccounts$getRecord.hasRecordedAccounts) return;
    glAccounts.readPushGlAccountsAsync.run(readAttemptId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled, readAttemptId, status]);
  const hasRead = isEnabled && glAccounts.hasRead;
  const cached = readAttemptId ? glAccounts.getRecordedAccounts(readAttemptId) : null;
  // Nothing recorded, while it is being asked again, is not yet an answer.
  const recorded = cached !== null && cached !== void 0 && cached.hasRecordedAccounts || !glAccounts.readPushGlAccountsAsync.inProgress ? cached : null;
  /**
   * A succeeded push answers from what it posted, read back or not. A pending one has posted nothing yet,
   * so it answers from its stored payload once there is one, and until then from the configured accounts
   * it will be built from.
   */
  const isRecorded = status === APPFOLIO_PUSH_STATUS.succeeded ? !!readAttemptId : !!(recorded !== null && recorded !== void 0 && recorded.hasRecordedAccounts);
  const getAccount = externalId => glAccounts.getGlAccountByExternalId(externalId);
  const isReadingUtilities = glAccounts.readPropertyUtilitiesAsync.inProgress;

  /**
   * The account one line would post to now. Its property utility unknown — not asked, or the asking
   * refused — falls back to the property's default; a pair with no property utility at all is none,
   * which is what blocks the push.
   */
  const configuredAccount = (propertyLivableId, utilityId) => {
    const propertyUtilityId = glAccounts.getPropertyUtilityId(propertyLivableId, utilityId);
    return propertyUtilityId === null ? null : glAccounts.getUtilityApGlAccount(propertyLivableId, propertyUtilityId);
  };

  /** Still being asked which property utility a line is: say nothing rather than name one and change it. */
  const isUnsettled = (propertyLivableId, utilityId) => isReadingUtilities && glAccounts.getPropertyUtilityId(propertyLivableId, utilityId) === undefined;

  /** Asked and not answered — refused, or failed — so the line is on its property's default. */
  const isOnDefault = (propertyLivableId, utilityId) => !isReadingUtilities && glAccounts.getPropertyUtilityId(propertyLivableId, utilityId) === undefined;
  return {
    isEnabled,
    /** Whether the labels describe what was posted rather than what is configured. */
    isRecorded,
    /**
     * Whether any of `pairs` could not be told its utility, and so shows its property's default. The
     * summary then says so: a utility of its own may post elsewhere, and this screen could not find out.
     */
    isOnPropertyDefault: pairs => !isRecorded && (pairs || []).some(pair => isOnDefault(pair.propertyId, pair.utilityId)),
    /** One line's account: its property's, as posted, once pushed; its own utility's before that. */
    getLineLabel: (propertyLivableId, utilityId) => {
      if (isRecorded) return getRecordedPropertyApGlLabel({
        recorded,
        propertyLivableId,
        getAccount
      });
      if (isUnsettled(propertyLivableId, utilityId)) return null;
      return getPropertyApGlLabel({
        account: configuredAccount(propertyLivableId, utilityId),
        hasRead
      });
    },
    /** The bill's account, when every one of `pairs` agrees. */
    getSummaryLabel: pairs => {
      if (isRecorded) return getRecordedSummaryApGlLabel({
        recorded,
        getAccount
      });
      if ((pairs || []).some(pair => isUnsettled(pair.propertyId, pair.utilityId))) {
        return getSummaryApGlLabel({
          lines: [],
          hasRead: false
        });
      }
      return getSummaryApGlLabel({
        lines: (pairs || []).map(pair => ({
          propertyLivableId: pair.propertyId,
          account: configuredAccount(pair.propertyId, pair.utilityId)
        })),
        hasRead
      });
    }
  };
};